import { forwardRef, useCallback, type MouseEvent, type ReactNode } from 'react';

import {
    Menu as AriakitMenu,
    MenuItem as AriakitMenuItem,
    MenuItemProps as AriakitMenuItemProps,
    MenuButton,
    MenuButtonProps,
    MenuProvider,
    useMenuStore,
} from '@ariakit/react';

import { colors } from '../../colors';
import { u } from '../../styles';
import { CaretCircleDownIcon, XIcon } from '../icons';
import { Box } from '../Layout';
import { Text } from '../Text';
import * as css from './Menu.css';

export const MenuItem = forwardRef<HTMLDivElement, AriakitMenuItemProps>(function MenuItem(props, ref) {
    return (
        <AriakitMenuItem ref={ref} {...props} className={css.menuItem({ disabled: props.disabled })}>
            {props?.children}
        </AriakitMenuItem>
    );
});

export interface MenuProps extends MenuButtonProps<'div'> {
    /** Title displayed in the menu header. */
    label: string;

    /** Horizontal alignment of the close button inside the menu header. @default 'left' */
    align?: 'left' | 'right';

    /** Size (in pixels) of the trigger and header icons. @default 24 */
    buttonSize?: number;

    /** Extra padding applied around the trigger button. @default 0 */
    buttonPadding?: number;

    /** Visual style variant of the menu. @default 'normal' */
    menuType?: 'main' | 'normal';

    /** Optional custom trigger content. */
    trigger?: ReactNode;

    /** Whether to unmount the menu content while it is hidden.*/
    unmountOnHide?: boolean;
}

export const Menu = ({
    label,
    children,
    align = 'left',
    buttonSize = 24,
    buttonPadding = 0,
    menuType = 'normal',
    trigger,
    unmountOnHide = true,
    ...props
}: MenuProps) => {
    const menu = useMenuStore();

    const shift = buttonPadding - buttonSize;
    const gutter = -(buttonSize + 24 + buttonPadding);
    const buttonStyle = buttonPadding
        ? {
              padding: buttonPadding,
              margin: -buttonPadding,
              borderBottomLeftRadius: '50%',
              outline: 'none',
          }
        : {};
    const handleHideMenu = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            menu.hide();
        },
        [menu],
    );
    return (
        <MenuProvider store={menu}>
            <MenuButton {...props} style={buttonStyle}>
                {trigger ?? (
                    <CaretCircleDownIcon
                        size={buttonSize}
                        color={colors.blue[500]}
                        weight="fill"
                        style={{ minWidth: buttonSize }}
                    />
                )}
            </MenuButton>

            <AriakitMenu
                className={css.menu({ menuType })}
                portal
                unmountOnHide={unmountOnHide}
                shift={shift}
                gutter={gutter}
            >
                {!menu.parent && (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        pt={u(3)}
                        paddingBlockEnd={label?.length > 0 ? u(2) : u(0.5)}
                        style={{ paddingInline: buttonSize }}
                    >
                        {align === 'right' && (
                            <button onClick={handleHideMenu}>
                                <XIcon size={buttonSize} />
                            </button>
                        )}
                        <Text variant={menuType === 'normal' ? 'h4' : 'h3'}>{label}</Text>
                        {align === 'left' && (
                            <button onClick={handleHideMenu}>
                                <CaretCircleDownIcon size={buttonSize} color={colors.blue[500]} weight="fill" />
                            </button>
                        )}
                    </Box>
                )}
                {children}
            </AriakitMenu>
        </MenuProvider>
    );
};
